import { utils } from 'ethers';
import React, { useMemo } from 'react';
import Pill, { PillTheme } from '@/components/UI/Pill';
import { TooltipWithRef } from '@/components/common/TooltipWithRef';
import BonusMultiplierBreakdown from '@/components/display/BonusMultiplierBreakdown';
import { TitleWithChildren } from '@/components/formatting/TitleWithChildren';
import type { BonusConfig } from '@/hooks/useBonusMultiplier';
import useBonusMultiplier from '@/hooks/useBonusMultiplier';
import type { PaymentCurrency_Enum } from '@/utilities/__generated__/graphql';
import { budgetFormatter } from '@/utilities/budgetFormatter';
import { getTailwindColor } from '@/utilities/tailwind';

export enum BudgetDisplayMode {
  WITH_TITLE,
  WITHOUT_TITLE,
}

interface BudgetPerAssetProps {
  address?: string | undefined;
  minBudget?: string | null | undefined;
  maxBudget?: string | null | undefined;
  royaltyPercentage?: number | undefined;
  paymentCurrency?: PaymentCurrency_Enum | undefined;
  collectionAddress?: string | null | undefined;
  bonusConfig?: BonusConfig | undefined;
  displayMode?: BudgetDisplayMode;
  isCreatingDeal?: boolean;
  publishedDate?: string | null | undefined;
  classes?: string;
}

export const BudgetPerAsset = ({
  address,
  paymentCurrency,
  minBudget,
  maxBudget,
  royaltyPercentage,
  collectionAddress,
  bonusConfig,
  displayMode = BudgetDisplayMode.WITH_TITLE,
  publishedDate,
  isCreatingDeal = false,
  classes,
}: BudgetPerAssetProps) => {
  const upfrontText = useMemo(
    () =>
      getUpfrontText(
        minBudget,
        maxBudget,
        royaltyPercentage,
        paymentCurrency,
        isCreatingDeal
      ),
    [minBudget, maxBudget, royaltyPercentage, paymentCurrency, isCreatingDeal]
  );

  const baseCollection = useMemo(
    () =>
      collectionAddress
        ? {
            name: 'Base Collection',
            nft_contract_address: collectionAddress,
          }
        : undefined,
    [collectionAddress]
  );

  const bonusCollection = useMemo(
    () =>
      bonusConfig?.contractAddress
        ? {
            name: 'Bonus Collection',
            nft_contract_address: bonusConfig?.contractAddress,
          }
        : undefined,
    [bonusConfig?.contractAddress]
  );

  const bonusResult = useBonusMultiplier({
    address,
    baseCollection,
    bonusCollection,
    basePercentage: royaltyPercentage,
    opportunityPublishedDate: publishedDate,
    bonusConfig,
  });

  const adjustedAmount = useMemo(
    () =>
      collectionAddress && displayMode === BudgetDisplayMode.WITH_TITLE
        ? bonusResult.adjustedAmount ?? 0
        : 0,
    [bonusResult.adjustedAmount, collectionAddress, displayMode]
  );

  if (displayMode === BudgetDisplayMode.WITHOUT_TITLE) {
    return (
      <div
        className={`flex flex-col ${royaltyPercentage ? 'text-[13px]' : ''}`}
      >
        <div>{upfrontText}</div>
        {royaltyPercentage && (
          <div className="mt-1">Royalty Percentage: {royaltyPercentage}%</div>
        )}
      </div>
    );
  }

  return (
    <TitleWithChildren title="Est. Budget Per Asset" classes={classes ?? ''}>
      <div className="flex flex-col items-start">
        {upfrontText && (
          <Pill
            theme={PillTheme.DEFAULT}
            showDot={false}
            classes="inline-flex mt-0"
          >
            {upfrontText}
          </Pill>
        )}
        {royaltyPercentage && (
          <TooltipWithRef
            label={
              bonusResult.rarityDetails?.length > 0 && (
                <BonusMultiplierBreakdown
                  rarityDetails={bonusResult.rarityDetails}
                  bonusConfig={bonusConfig}
                  baseRoyaltyPercentage={royaltyPercentage}
                  adjustedRoyaltyPercentage={adjustedAmount}
                />
              )
            }
            c={getTailwindColor('neutral')['70']}
            color={getTailwindColor('primary').surface}
            transitionProps={{ transition: 'fade', duration: 500 }}
            withArrow
            arrowSize={8}
            openDelay={300}
            position={'top'}
          >
            <Pill
              theme={PillTheme.ORANGE}
              showDot={bonusResult.bonusMultiplier !== adjustedAmount}
              pulse={bonusResult.bonusMultiplier !== adjustedAmount}
              classes="mt-0 inline-flex"
            >
              Royalty Percentage: {adjustedAmount}%
            </Pill>
          </TooltipWithRef>
        )}
      </div>
    </TitleWithChildren>
  );
};

const getUpfrontText = (
  minBudget: string | null | undefined,
  maxBudget: string | null | undefined,
  royaltyPercentage: number | undefined,
  paymentCurrency: PaymentCurrency_Enum | undefined,
  isCreatingDeal: boolean
): string => {
  if (!minBudget || !maxBudget) return '';
  const formattedMinBudget = isCreatingDeal
    ? utils.parseUnits(minBudget, 6).toString()
    : minBudget;
  const formattedMaxBudget = isCreatingDeal
    ? utils.parseUnits(maxBudget, 6).toString()
    : maxBudget;
  return `${royaltyPercentage ? 'Upfront Payment: ' : ''}${budgetFormatter(
    formattedMinBudget,
    formattedMaxBudget,
    paymentCurrency
  )}`;
};
